import { useState, useEffect, useRef } from 'react';
import { Trophy, Crown } from 'lucide-react';
import confetti from 'canvas-confetti';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const SPORT_META = {
  AFL: { colour: '#E00000', label: 'AFL' },
  NRL: { colour: '#007A33', label: 'NRL' },
  EPL: { colour: '#37003C', label: 'EPL' },
  WC:  { colour: '#4338CA', label: 'World Cup' },
};

// Confetti palette per sport — gold + team colour for a champion feel.
const CONFETTI_PALETTE = {
  AFL: ['#FFD700', '#E00000', '#FFFFFF'],
  NRL: ['#FFD700', '#007A33', '#FFFFFF'],
  EPL: ['#FFD700', '#37003C', '#FFFFFF'],
  WC:  ['#FFD700', '#4338CA', '#FFFFFF'],
};

const STORAGE_KEY = 'hof_last_seen_v1';  // {competition_id: awarded_at ISO}

function loadSeen() {
  try {
    return JSON.parse(localStorage.getItem(STORAGE_KEY) || '{}');
  } catch {
    return {};
  }
}

function saveSeen(map) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(map));
  } catch { /* quota — ignore */ }
}

// Fire a subtle confetti burst anchored to a specific card element.
function burstAt(cardEl, sport) {
  if (!cardEl) return;
  const rect = cardEl.getBoundingClientRect();
  const x = (rect.left + rect.width / 2) / window.innerWidth;
  const y = (rect.top + rect.height / 2) / window.innerHeight;
  const colors = CONFETTI_PALETTE[sport] || CONFETTI_PALETTE.WC;
  // Two-stage burst: primary + confetto-sprinkle for a tasteful champion feel.
  confetti({
    particleCount: 60,
    spread: 70,
    startVelocity: 32,
    origin: { x, y },
    colors,
    scalar: 0.9,
    ticks: 180,
    disableForReducedMotion: true,
  });
  setTimeout(() => {
    confetti({
      particleCount: 40,
      spread: 100,
      startVelocity: 22,
      origin: { x, y },
      colors,
      scalar: 0.7,
      ticks: 220,
      disableForReducedMotion: true,
    });
  }, 220);
}

export default function HallOfFame() {
  const [champions, setChampions] = useState([]);
  const [loading, setLoading] = useState(true);
  const cardRefs = useRef({});     // competition_id → HTMLElement
  const firedRef = useRef(false);  // guard against double-fires (e.g. StrictMode)

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await axios.get(`${API_URL}/api/champions`);
        if (!cancelled) setChampions(res.data || []);
      } catch (err) {
        console.error('Error fetching Hall of Fame:', err);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // Once cards are rendered, look for any champion whose `awarded_at` is
  // newer than the last-seen timestamp in localStorage and fire confetti on
  // that card. Persist the new timestamp so the burst only plays once per
  // browser per title.
  useEffect(() => {
    if (loading || champions.length === 0 || firedRef.current) return;
    firedRef.current = true;
    const seen = loadSeen();
    const toBurst = champions.filter((c) => {
      if (!c.awarded_at) return false;
      const prev = seen[c.competition_id];
      return !prev || new Date(c.awarded_at) > new Date(prev);
    });
    if (toBurst.length === 0) return;
    // Stagger multiple bursts so they read as a sequence, not a blast.
    toBurst.forEach((c, i) => {
      setTimeout(() => {
        const el = cardRefs.current[c.competition_id];
        burstAt(el, c.sport);
      }, 350 + i * 700);
    });
    const next = { ...seen };
    toBurst.forEach((c) => { next[c.competition_id] = c.awarded_at; });
    saveSeen(next);
  }, [loading, champions]);

  if (loading) return null;
  if (champions.length === 0) return null;

  return (
    <section className="mb-10" data-testid="hall-of-fame">
      <div className="flex items-center gap-3 mb-4">
        <Crown className="w-5 h-5 text-[#FFD700]" strokeWidth={2.5} />
        <h2 className="text-sm uppercase tracking-widest font-black text-[#1A1A1A]">Hall of Fame</h2>
        <div className="flex-1 h-px bg-gradient-to-r from-[#FFD700]/60 to-transparent" />
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {champions.map((c) => {
          const meta = SPORT_META[c.sport] || { colour: '#B8860B', label: c.sport };
          return (
            <div
              key={c.competition_id}
              ref={(el) => { cardRefs.current[c.competition_id] = el; }}
              className="relative tipster-card p-0 overflow-hidden group"
              data-testid={`hall-of-fame-card-${c.competition_id}`}
            >
              {/* Coloured sport strip */}
              <div className="h-1.5 w-full" style={{ backgroundColor: meta.colour }} />
              <div className="p-5 flex items-start gap-4">
                <div
                  className="w-14 h-14 rounded-full flex items-center justify-center text-white text-xl font-black flex-shrink-0 shadow-md"
                  style={{ backgroundColor: meta.colour }}
                >
                  {c.picture ? (
                    <img src={c.picture} alt={c.name} className="w-full h-full rounded-full object-cover" />
                  ) : (
                    c.name?.charAt(0) || '?'
                  )}
                </div>
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-1.5 mb-0.5">
                    <span
                      className="text-[10px] uppercase tracking-widest font-bold"
                      style={{ color: meta.colour }}
                    >
                      {meta.label} Champion
                    </span>
                  </div>
                  <div className="text-xl font-black text-[#1A1A1A] leading-tight truncate" data-testid={`hall-of-fame-name-${c.competition_id}`}>
                    {c.name}
                  </div>
                  <div className="text-xs text-gray-500 mt-1 truncate">{c.title}</div>
                  <div className="flex items-center gap-3 mt-2">
                    <span className="inline-flex items-center gap-1 text-xs font-bold text-[#FFB81C]">
                      <Trophy className="w-3.5 h-3.5" fill="#FFD700" strokeWidth={2} />
                      {Math.round(c.points)} pts
                    </span>
                  </div>
                </div>
              </div>
              {/* Subtle gold shimmer accent */}
              <div className="absolute top-0 right-0 w-24 h-24 -mr-8 -mt-8 rounded-full bg-[#FFD700]/10 blur-2xl pointer-events-none group-hover:bg-[#FFD700]/20 transition-colors" />
            </div>
          );
        })}
      </div>
    </section>
  );
}
